/**
 * Course *name* rules (distinct from the 7-segment `Course.SKU`).
 *
 * MIRRORED FILE — keep in step with `backend/src/graphql/utils/courseName.ts`.
 * The two copies are byte-identical, as `media.ts` already is (see CLAUDE.md). The tests are
 * mirrored too, which is what stops them drifting.
 *
 * The convention, from real production names:
 *
 *   NAME     := CENTRE "-" SUBJECT [ "-" LEVEL ] "-" SCHEDULE
 *   CENTRE   := 2-3 uppercase letters                     DH, KP, SG
 *   SUBJECT  := 1+ segments joined by "-"                 Basic, HW Club, Boys-Football, P.Patterns
 *   LEVEL    := [A-Z][0-9]+[a-z]?  (optional)             L1, L2a, B1, E3, G1
 *   SCHEDULE := SLOT ( "+" SLOT )*
 *   SLOT     := DAY "_" TIME                              Tue_15.00
 *   DAY      := Mon|Tue|Wed|Thu|Fri|Sat|Sun|Daily
 *   TIME     := HH "." MM, 24-hour, both parts 2 digits   09.00, 15.30
 *
 * Parsing is anchored from BOTH ends: the first segment is the centre, the last is the
 * schedule, and the second-to-last is the level only if it looks like one. Everything between
 * is the subject. That is what makes `DH-Boys-Football-L2-Daily_17.00` (a dash inside the
 * subject) and `SG-Phonics-Mon_13.00+Wed_13.00` (no level at all) both parse — a naive
 * four-segment split gets both wrong, and both exist in production.
 *
 * Timezone: `suggestNameFromSessions` reads the day and time off each session with
 * `Date#getDay`/`getHours`, i.e. in whatever zone the caller runs in. It is meant to be called
 * from the browser, so the suggestion always agrees with the session times rendered next to it
 * on the same screen. The backend copy exists for the format half of this module only.
 */

export const DAY_CODES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

/** `Daily` means Mon-Fri, matching the schedule parser the TimeTable page already used. */
export const DAILY_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] as const;

/** Below this many future sessions a class is nearly over, so we stop suggesting renames. */
export const MIN_FUTURE_SESSIONS_FOR_SUGGESTION = 5;

/** A day/time must recur this often in the remaining sessions before it can join the name. */
export const MIN_OCCURRENCES_FOR_RECURRING = 2;

export type WeekDayCode = typeof DAY_CODES[number];
export type DayCode = WeekDayCode | 'Daily';

export type Slot = { day: DayCode; hours: number; minutes: number };

export type ParsedCourseName = {
  centre: string;
  subject: string;
  level: string | null;
  slots: Slot[];
};

export type SessionLike = {
  startTime: number | string | Date;
  reasonForCancellation?: string | null;
};

const CENTRE_RE = /^[A-Z]{2,3}$/;
const LEVEL_RE = /^[A-Z][0-9]+[a-z]?$/;
const LEVEL_LENIENT_RE = /^([A-Za-z])([0-9]+)([A-Za-z])?$/;
const SUBJECT_SEGMENT_RE = /^[A-Za-z0-9][A-Za-z0-9 .&]*$/;
const TIME_RE = /^([01][0-9]|2[0-3])\.([0-5][0-9])$/;
const LENIENT_TIME_RE = /^([0-9]{1,2})(?:[.:;,]([0-9]{2}))?\s*(am|pm)?$/i;
/** Words that join two days rather than naming one: `Wed_&_Fri_16.00`. */
const JOINER_RE = /^(&|and)$/i;

const DAY_ALIASES: Record<string, DayCode> = {
  mon: 'Mon', monday: 'Mon',
  tue: 'Tue', tues: 'Tue', tuesday: 'Tue',
  wed: 'Wed', weds: 'Wed', wednesday: 'Wed',
  thu: 'Thu', thur: 'Thu', thurs: 'Thu', thursday: 'Thu',
  fri: 'Fri', friday: 'Fri',
  sat: 'Sat', saturday: 'Sat',
  sun: 'Sun', sunday: 'Sun',
  daily: 'Daily',
};

const pad2 = (n: number): string => String(n).padStart(2, '0');

const dayIndex = (day: DayCode): number => (day === 'Daily' ? -1 : DAY_CODES.indexOf(day));

const minutesOfDay = (slot: Slot): number => slot.hours * 60 + slot.minutes;

export const formatTime = (hours: number, minutes: number): string => `${pad2(hours)}.${pad2(minutes)}`;

export const formatSlot = (slot: Slot): string => `${slot.day}_${formatTime(slot.hours, slot.minutes)}`;

const sortSlots = (slots: Slot[]): Slot[] =>
  [...slots].sort((a, b) => dayIndex(a.day) - dayIndex(b.day) || minutesOfDay(a) - minutesOfDay(b));

const buildSchedule = (slots: Slot[]): string => sortSlots(slots).map(formatSlot).join('+');

export const buildCourseName = (parsed: ParsedCourseName): string =>
  [parsed.centre, parsed.subject, ...(parsed.level ? [parsed.level] : []), buildSchedule(parsed.slots)].join('-');

// ---------------------------------------------------------------------------
// Strict parsing
// ---------------------------------------------------------------------------

const parseSlotStrict = (raw: string): Slot | null => {
  const separator = raw.indexOf('_');
  if (separator === -1) return null;

  const day = raw.slice(0, separator);
  const time = raw.slice(separator + 1);

  if (day !== 'Daily' && !(DAY_CODES as readonly string[]).includes(day)) return null;

  const match = TIME_RE.exec(time);
  if (match == null) return null;

  return { day: day as DayCode, hours: Number(match[1]), minutes: Number(match[2]) };
};

const parseScheduleStrict = (raw: string): Slot[] | null => {
  if (raw === '') return null;

  const slots: Slot[] = [];
  for (const part of raw.split('+')) {
    const slot = parseSlotStrict(part);
    if (slot == null) return null;
    slots.push(slot);
  }

  // `Daily` already means Mon-Fri, so combining it with other days is contradictory.
  if (slots.some((s) => s.day === 'Daily') && slots.length > 1) return null;

  const keys = slots.map(formatSlot);
  if (new Set(keys).size !== keys.length) return null;

  return slots;
};

/** Strict: returns null unless `name` already conforms exactly, canonical slot order included. */
export const parseCourseName = (name: string): ParsedCourseName | null => {
  if (typeof name !== 'string' || name !== name.trim() || name === '') return null;

  const segments = name.split('-');
  if (segments.length < 3) return null;

  const centre = segments[0];
  if (!CENTRE_RE.test(centre)) return null;

  const scheduleRaw = segments[segments.length - 1];
  const slots = parseScheduleStrict(scheduleRaw);
  if (slots == null) return null;
  if (buildSchedule(slots) !== scheduleRaw) return null;

  let middle = segments.slice(1, -1);

  // Only treat the last middle segment as a level if something is left over for the subject —
  // a name always needs a subject, so `SG-L1-Mon_10.00` reads as subject "L1", not a bare level.
  let level: string | null = null;
  if (middle.length >= 2 && LEVEL_RE.test(middle[middle.length - 1])) {
    level = middle[middle.length - 1];
    middle = middle.slice(0, -1);
  }

  if (middle.length === 0) return null;
  if (!middle.every((segment) => segment === segment.trim() && SUBJECT_SEGMENT_RE.test(segment))) return null;

  return { centre, subject: middle.join('-'), level, slots };
};

// ---------------------------------------------------------------------------
// Lenient rewriting (the "did you mean" engine)
// ---------------------------------------------------------------------------

const normaliseDay = (word: string): DayCode | null =>
  DAY_ALIASES[word.trim().toLowerCase().replace(/\.$/, '')] ?? null;

const normaliseTime = (word: string): { hours: number; minutes: number } | null => {
  const match = LENIENT_TIME_RE.exec(word.trim());
  if (match == null) return null;

  let hours = Number(match[1]);
  const minutes = match[2] ? Number(match[2]) : 0;
  const meridiem = match[3]?.toLowerCase();

  if (meridiem) {
    if (hours < 1 || hours > 12) return null;
    if (meridiem === 'pm' && hours !== 12) hours += 12;
    if (meridiem === 'am' && hours === 12) hours = 0;
  }

  if (hours > 23 || minutes > 59) return null;

  return { hours, minutes };
};

const normaliseLevel = (word: string): string | null => {
  const match = LEVEL_LENIENT_RE.exec(word);
  if (match == null) return null;
  return `${match[1].toUpperCase()}${match[2]}${match[3] ? match[3].toLowerCase() : ''}`;
};

/**
 * Best-effort rewrite. Splits on every separator that has ever been used as one (`-`, `+`, `_`)
 * and rebuilds, pairing day/time words from the right. Losing the original separators is the
 * point: it is what fixes `KP_NailExt_L1_Wed_12.00`, `Wed_15.30-Fri_15.30` and
 * `Tue_11.00_+Fri_11.00` with one pass rather than a rule each.
 *
 * May return something still invalid — callers decide whether to trust it. `autoFixCourseName`
 * is the gated version.
 */
export const rewriteCourseName = (name: string): string => {
  if (typeof name !== 'string') return '';

  const words = name
    .split(/[-+_]/)
    .map((word) => word.trim().replace(/ {2,}/g, ' '))
    .filter((word) => word !== '');

  if (words.length === 0) return '';

  // Pair (day, time) from the right for as long as the words keep looking like slots. A subject
  // word cannot be mistaken for a day unless it *is* a bare day name, and the pairing stops at
  // the first word that is not one.
  const slots: Slot[] = [];
  let head = words.length;
  while (head >= 2) {
    // A joiner sitting between two whole slots: `Wed_07:00_&_Fri_07:00`.
    if (JOINER_RE.test(words[head - 1])) {
      head -= 1;
      continue;
    }

    const day = normaliseDay(words[head - 2]);
    const time = normaliseTime(words[head - 1]);
    if (day == null || time == null) break;
    slots.unshift({ day, ...time });
    head -= 2;

    // A run of bare day names sharing one time, as in `Mon_Wed_Fri_18.00` and
    // `Wed_&_Fri_16.00` (both real). Without this the extra days get absorbed into the
    // subject, producing a name that passes validation while saying the class runs one day a
    // week instead of three. The joiner is only consumed when a day really does precede it,
    // so an `&` that is part of a subject (`RSB_&_JG_Self_Defense`) is left alone.
    while (head >= 1) {
      let cursor = head - 1;
      if (JOINER_RE.test(words[cursor]) && cursor >= 1) cursor -= 1;
      const sharedDay = normaliseDay(words[cursor]);
      if (sharedDay == null) break;
      slots.unshift({ day: sharedDay, ...time });
      head = cursor;
    }
  }

  const headWords = words.slice(0, head);
  if (headWords.length === 0 || slots.length === 0) return name.trim();

  const centre = headWords[0].toUpperCase();
  let rest = headWords.slice(1);

  let level: string | null = null;
  if (rest.length >= 2) {
    const candidate = normaliseLevel(rest[rest.length - 1]);
    if (candidate != null) {
      level = candidate;
      rest = rest.slice(0, -1);
    }
  }

  if (rest.length === 0) return name.trim();

  const deduped: Slot[] = [];
  const seen = new Set<string>();
  for (const slot of slots) {
    const key = formatSlot(slot);
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(slot);
  }

  // `Daily` alongside named days is contradictory; keep the explicit days and drop `Daily`.
  const hasNamedDay = deduped.some((slot) => slot.day !== 'Daily');
  const resolved = hasNamedDay ? deduped.filter((slot) => slot.day !== 'Daily') : deduped;

  return buildCourseName({ centre, subject: rest.join('-'), level, slots: resolved });
};

/**
 * Tier A. The safety gate: only returns a rewrite that lands on a fully valid name, so the
 * silent on-blur path can never leave a half-fixed name behind. Returns null when there is
 * nothing to do, or when rewriting cannot reach a valid name.
 */
export const autoFixCourseName = (name: string): string | null => {
  const rewritten = rewriteCourseName(name);
  if (rewritten === name) return null;
  return parseCourseName(rewritten) == null ? null : rewritten;
};

/** Tier B. Best-effort fix for display as "did you mean"; may still be invalid. */
export const suggestCourseName = (name: string): string | null => {
  const rewritten = rewriteCourseName(name);
  return rewritten === name || rewritten === '' ? null : rewritten;
};

// ---------------------------------------------------------------------------
// Validation messages
// ---------------------------------------------------------------------------

const EXAMPLE_NAME = 'SG-Bengali-L1-Mon_10.00+Thu_10.00';

const describeSlotProblem = (raw: string): string | null => {
  const separator = raw.indexOf('_');
  if (separator === -1) {
    return `"${raw}" needs a day and a time joined by "_", like Tue_15.00`;
  }

  const day = raw.slice(0, separator);
  const time = raw.slice(separator + 1);

  if (day !== 'Daily' && !(DAY_CODES as readonly string[]).includes(day)) {
    const fixed = normaliseDay(day);
    return fixed != null
      ? `Write the day as "${fixed}", not "${day}"`
      : `"${day}" is not a day — use ${DAY_CODES.join(', ')} or Daily`;
  }

  if (!TIME_RE.test(time)) {
    if (time.includes(':')) return `Times use "." not ":" — write "${time.replace(':', '.')}"`;
    if (/(am|pm)$/i.test(time)) {
      const fixed = normaliseTime(time);
      return fixed != null
        ? `Times are 24-hour — write "${formatTime(fixed.hours, fixed.minutes)}" not "${time}"`
        : `"${time}" is not a 24-hour time`;
    }
    const fixed = normaliseTime(time);
    return fixed != null
      ? `Times need two digits either side of the "." — write "${formatTime(fixed.hours, fixed.minutes)}" not "${time}"`
      : `"${time}" is not a 24-hour time like 09.00 or 15.30`;
  }

  return null;
};

const describeScheduleProblem = (raw: string): string[] => {
  const errors: string[] = [];
  const parts = raw.split('+');

  for (const part of parts) {
    const problem = describeSlotProblem(part);
    if (problem != null) errors.push(problem);
  }

  if (errors.length > 0) return errors;

  const slots = parts.map(parseSlotStrict).filter((s): s is Slot => s != null);

  if (slots.some((s) => s.day === 'Daily') && slots.length > 1) {
    errors.push('"Daily" already means Mon-Fri, so it cannot be combined with other days');
  }

  const keys = slots.map(formatSlot);
  const duplicate = keys.find((key, index) => keys.indexOf(key) !== index);
  if (duplicate != null) errors.push(`"${duplicate}" appears twice`);

  if (errors.length === 0 && buildSchedule(slots) !== raw) {
    errors.push(`Days should run Monday first — write "${buildSchedule(slots)}"`);
  }

  return errors;
};

export const validateCourseName = (name: string): { valid: boolean; errors: string[] } => {
  if (parseCourseName(name) != null) return { valid: true, errors: [] };

  const errors: string[] = [];
  const raw = typeof name === 'string' ? name : '';
  const trimmed = raw.trim();

  if (trimmed === '') return { valid: false, errors: ['A class name is required'] };

  if (trimmed !== raw) errors.push('Remove the space at the start or end of the name');

  const segments = trimmed.split('-');

  if (!CENTRE_RE.test(segments[0])) {
    errors.push(
      `"${segments[0]}" is not a centre code — a name starts with 2 or 3 capitals, like ${EXAMPLE_NAME}`,
    );
  }

  if (segments.length < 3) {
    errors.push(`A name needs a centre, a subject and a day/time joined by "-", like ${EXAMPLE_NAME}`);
    return { valid: false, errors };
  }

  errors.push(...describeScheduleProblem(segments[segments.length - 1]));

  let middle = segments.slice(1, -1);
  if (middle.length >= 2 && LEVEL_RE.test(middle[middle.length - 1])) {
    middle = middle.slice(0, -1);
  } else if (middle.length >= 2) {
    const candidate = normaliseLevel(middle[middle.length - 1]);
    if (candidate != null) {
      errors.push(`Write the level as "${candidate}", not "${middle[middle.length - 1]}"`);
      middle = middle.slice(0, -1);
    }
  }

  for (const segment of middle) {
    if (segment.trim() === '') {
      errors.push('The name has an empty part — check for a doubled "-"');
    } else if (segment !== segment.trim()) {
      errors.push(`Remove the space around "${segment.trim()}"`);
    } else if (!SUBJECT_SEGMENT_RE.test(segment)) {
      errors.push(`"${segment}" cannot be part of the subject — letters, numbers, spaces, "." and "&" only`);
    }
  }

  if (errors.length === 0) {
    errors.push(`"${trimmed}" does not match the expected format, like ${EXAMPLE_NAME}`);
  }

  return { valid: false, errors };
};

// ---------------------------------------------------------------------------
// Reconciling the name against the course's future sessions
// ---------------------------------------------------------------------------

const toMillis = (value: number | string | Date): number | null => {
  if (value instanceof Date) return value.getTime();
  const millis = typeof value === 'string' ? Number(value) : value;
  return typeof millis === 'number' && Number.isFinite(millis) ? millis : null;
};

/** Expands a slot list to concrete `Day_HH.MM` keys, turning `Daily` into its five weekdays. */
export const expandSlots = (slots: Slot[]): string[] => {
  const keys: string[] = [];
  for (const slot of slots) {
    const days: readonly DayCode[] = slot.day === 'Daily' ? DAILY_DAYS : [slot.day];
    for (const day of days) {
      const key = `${day}_${formatTime(slot.hours, slot.minutes)}`;
      if (!keys.includes(key)) keys.push(key);
    }
  }
  return keys;
};

/**
 * Sessions are routinely backfilled in retrospect when classes merge, so only sessions still to
 * come say anything about what the name should be.
 */
export const futureSessions = <T extends SessionLike>(sessions: T[], now: number): T[] =>
  (sessions ?? []).filter((session) => {
    const millis = toMillis(session.startTime);
    const cancelled = session.reasonForCancellation != null && session.reasonForCancellation !== '';
    return millis != null && millis > now && !cancelled;
  });

export const sessionSlotKey = (millis: number): string => {
  const date = new Date(millis);
  // JS weeks start on Sunday; ours start on Monday.
  const day = DAY_CODES[(date.getDay() + 6) % 7];
  return `${day}_${formatTime(date.getHours(), date.getMinutes())}`;
};

export type ScheduleSuggestion = {
  suggestedName: string;
  removals: string[];
  additions: string[];
};

/**
 * Compares the day/time half of a name against the course's remaining sessions.
 * Returns null when the name is not yet valid, when fewer than
 * MIN_FUTURE_SESSIONS_FOR_SUGGESTION sessions remain, or when there is nothing to change.
 */
export const suggestNameFromSessions = (
  name: string,
  sessions: SessionLike[],
  now: number = Date.now(),
): ScheduleSuggestion | null => {
  const parsed = parseCourseName(name);
  if (parsed == null) return null;

  const upcoming = futureSessions(sessions, now);
  if (upcoming.length < MIN_FUTURE_SESSIONS_FOR_SUGGESTION) return null;

  const counts = new Map<string, number>();
  for (const session of upcoming) {
    const millis = toMillis(session.startTime);
    if (millis == null) continue;
    const key = sessionSlotKey(millis);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  const nameKeys = expandSlots(parsed.slots);

  const removals = nameKeys.filter((key) => !counts.has(key));

  // One ad-hoc catch-up session must never rewrite the name.
  const additions = Array.from(counts.entries())
    .filter(([key, count]) => count >= MIN_OCCURRENCES_FOR_RECURRING && !nameKeys.includes(key))
    .map(([key]) => key);

  if (removals.length === 0 && additions.length === 0) return null;

  const finalKeys = [...nameKeys.filter((key) => !removals.includes(key)), ...additions];
  if (finalKeys.length === 0) return null;

  let slots = finalKeys.map(parseSlotStrict).filter((slot): slot is Slot => slot != null);

  // Only re-collapse to `Daily` if the name was already written that way — otherwise a
  // five-day name the author spelled out would come back rewritten for no reason.
  if (parsed.slots.some((slot) => slot.day === 'Daily')) {
    const times = new Set(slots.map((slot) => formatTime(slot.hours, slot.minutes)));
    const days = new Set(slots.map((slot) => slot.day));
    if (times.size === 1 && days.size === DAILY_DAYS.length && DAILY_DAYS.every((day) => days.has(day))) {
      slots = [{ day: 'Daily', hours: slots[0].hours, minutes: slots[0].minutes }];
    }
  }

  const suggestedName = buildCourseName({ ...parsed, slots });
  if (suggestedName === name) return null;

  return { suggestedName, removals, additions };
};
